import { useEffect, useRef, useState, type DragEvent } from "react";
import clsx from "clsx";
import {
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/Button";
import { setGlobalUploadSuppressed } from "@/features/globalUpload/globalUploadSuppress";
import { openMaintenanceContractDocument, type MaintenanceContractDocument } from "@/lib/wartungen";

export const CONTRACT_DOCUMENT_FOLDER_LABEL = "InfoHub › Wartungen › Verträge";

function formatBytes(bytes?: number | null): string {
  const n = Number(bytes || 0);
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}

function formatUploadedAt(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function isPdf(file: File): boolean {
  return file.type === "application/pdf" || /\.pdf$/i.test(file.name);
}

export interface ContractDocumentCardProps {
  /** Vertrags-ID; `null` beim Anlegen (Datei wird dann erst nach dem Speichern hochgeladen). */
  contractId: number | null;
  document: MaintenanceContractDocument | null;
  /** Beim Anlegen: vorgemerkte Datei, die nach dem Speichern hochgeladen wird. */
  pendingFile: File | null;
  onPendingFileChange: (file: File | null) => void;
  onUpload: (file: File) => Promise<void>;
  onRemove: () => Promise<void>;
  busy?: boolean;
  className?: string;
}

/**
 * Vertragsdokument (PDF) im InfoHub: anzeigen, hochladen/ersetzen, entfernen.
 * Beim Anlegen eines Vertrags wird die Datei vorgemerkt und direkt nach dem
 * ersten Speichern hochgeladen.
 */
export function ContractDocumentCard({
  contractId,
  document,
  pendingFile,
  onPendingFileChange,
  onUpload,
  onRemove,
  busy = false,
  className,
}: ContractDocumentCardProps) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const dropCounterRef = useRef(0);
  const [dropActive, setDropActive] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setGlobalUploadSuppressed(true);
    return () => setGlobalUploadSuppressed(false);
  }, []);

  function openDocument(download: boolean) {
    if (contractId == null) return;
    setOpening(true);
    setError(null);
    void openMaintenanceContractDocument(contractId, { download, filename: document?.filename })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "PDF konnte nicht geöffnet werden.");
      })
      .finally(() => setOpening(false));
  }

  function pick(list: FileList | null) {
    const file = list && list.length ? list[0] : null;
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setError(null);
    if (!isPdf(file)) {
      setError("Bitte eine PDF-Datei auswählen.");
      return;
    }
    if (contractId == null) {
      onPendingFileChange(file);
      return;
    }
    void onUpload(file).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : "Dokument konnte nicht hochgeladen werden.");
    });
  }

  function hasFiles(event: DragEvent): boolean {
    return Array.from(event.dataTransfer?.types || []).includes("Files");
  }

  const dropHandlers = {
    onDragEnter: (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      if (busy) return;
      dropCounterRef.current += 1;
      setDropActive(true);
    },
    onDragOver: (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = busy ? "none" : "copy";
    },
    onDragLeave: (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dropCounterRef.current = Math.max(0, dropCounterRef.current - 1);
      if (dropCounterRef.current === 0) setDropActive(false);
    },
    onDrop: (event: DragEvent) => {
      if (!hasFiles(event)) return;
      event.preventDefault();
      event.stopPropagation();
      dropCounterRef.current = 0;
      setDropActive(false);
      if (busy) return;
      pick(event.dataTransfer.files);
    },
  };

  const hasDocument = Boolean(document) && contractId != null;

  return (
    <section className={clsx("rounded-ios border border-separator bg-card p-4 shadow-ios", className)} {...dropHandlers}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-label">Vertragsdokument</h3>
          <p className="mt-0.5 text-xs text-label-secondary">
            Der unterschriebene Vertrag als PDF. Ablage in SharePoint unter {CONTRACT_DOCUMENT_FOLDER_LABEL}.
          </p>
        </div>
        <DocumentTextIcon className={clsx("h-6 w-6 shrink-0", hasDocument ? "text-accent" : "text-label-tertiary")} aria-hidden />
      </div>

      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept=".pdf,application/pdf"
        onChange={(e) => pick(e.target.files)}
      />

      {hasDocument && document ? (
        <div className="mt-3 rounded-lg border border-separator bg-fill-quaternary/30 p-3">
          <p className="truncate text-sm font-medium text-label" title={document.filename}>
            {document.filename}
          </p>
          <p className="mt-0.5 text-xs text-label-tertiary">
            {[formatBytes(document.size_bytes), formatUploadedAt(document.uploaded_at), document.uploaded_by]
              .filter(Boolean)
              .join(" · ")}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              disabled={busy || opening}
              className="inline-flex h-8 items-center gap-1 rounded-lg bg-accent px-2.5 text-xs font-medium text-label-on-accent hover:bg-accent-hover disabled:opacity-50"
              onClick={() => openDocument(false)}
            >
              <DocumentTextIcon className="h-4 w-4" aria-hidden />
              Öffnen
            </button>
            <button
              type="button"
              disabled={busy || opening}
              className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label disabled:opacity-50"
              title="PDF herunterladen"
              onClick={() => openDocument(true)}
            >
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
              Download
            </button>
            {document.web_url ? (
              <a
                href={document.web_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-label-secondary hover:bg-fill-tertiary hover:text-label"
                title="In SharePoint öffnen"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden />
                SharePoint
              </a>
            ) : null}
            <span className="ml-auto flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={busy}
                leftIcon={<ArrowUpTrayIcon className="h-4 w-4" aria-hidden />}
                onClick={() => fileRef.current?.click()}
              >
                Ersetzen
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-status-red"
                disabled={busy}
                leftIcon={<TrashIcon className="h-4 w-4" aria-hidden />}
                onClick={() => {
                  if (!window.confirm("Vertragsdokument wirklich entfernen? Die Datei wird auch aus SharePoint gelöscht.")) return;
                  setError(null);
                  void onRemove().catch((err: unknown) => {
                    setError(err instanceof Error ? err.message : "Dokument konnte nicht entfernt werden.");
                  });
                }}
              >
                Entfernen
              </Button>
            </span>
          </div>
        </div>
      ) : pendingFile ? (
        <div className="mt-3 rounded-lg border border-accent/40 bg-accent/5 p-3">
          <p className="truncate text-sm font-medium text-label" title={pendingFile.name}>
            {pendingFile.name}
          </p>
          <p className="mt-0.5 text-xs text-label-secondary">
            {formatBytes(pendingFile.size)} · wird nach dem Anlegen des Vertrags hochgeladen.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => fileRef.current?.click()}>
              Andere Datei
            </Button>
            <Button type="button" variant="ghost" size="sm" className="text-status-red" disabled={busy} onClick={() => onPendingFileChange(null)}>
              Verwerfen
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className={clsx(
            "mt-3 flex w-full flex-col items-center justify-center rounded-lg border border-dashed px-4 py-5 text-center transition",
            busy ? "cursor-wait opacity-70" : "cursor-pointer",
            dropActive
              ? "border-accent bg-accent/10 text-label"
              : "border-separator text-label-secondary hover:border-accent hover:bg-accent/5",
          )}
        >
          <ArrowUpTrayIcon className="h-5 w-5 text-label-tertiary" aria-hidden />
          <span className="mt-1.5 text-sm font-medium text-label">{busy ? "Wird hochgeladen …" : "PDF hierher ziehen"}</span>
          <span className="mt-0.5 text-xs text-label-tertiary">oder klicken zum Auswählen · max. 25 MB</span>
        </button>
      )}

      {error ? <p className="mt-2 text-xs text-status-red">{error}</p> : null}
    </section>
  );
}
